import React, {FC, memo, ReactNode, useCallback, useLayoutEffect, useRef} from 'react';
import styled, {css} from 'styled-components';

import {useUniqueElementId} from '../../../../core/src/helpers/react/hooks/uniqueIdHelpers';
import {FrontChangeEvent} from '../../../../core/src/helpers/react/reactHelpers';
import {PaletteColorsEnum} from '../../../../core/src/models/api/paletteModel';
import {VisualSizesEnum} from '../../styles/commonStyles';
import {appearanceNone, center} from '../../styles/mixins';
import {makeSizeConstants} from '../../styles/styleHelpers';
import {TooltipCoordinator} from '../tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../tooltips/tooltipOverflowCondition';
import {BooleanInputProps} from './booleanInputTypes';

/*
 * Props.
 */

interface BooleanInputWithLabelProps extends BooleanInputProps {
  /** The type of input to render. */
  type: string;
  /** The size of input to render. */
  size: `${VisualSizesEnum}`;
  /** The content to render as the isChecked indicator. */
  checkedIndicator: ReactNode;
  /** Whether to render the indicator as a circle. */
  isRoundedIndicator?: boolean;
  children?: ReactNode;
  isOnRightSide?: boolean;
  isMultiline?: boolean;
  /** Whether the indicator centers on the whole label rather than on its first line. */
  shouldCenterLabel?: boolean;
}

/*
 * Style.
 */

const StyledWrapperLabel = styled.label<{$shouldCenterLabel?: boolean}>`
  display: flex;
  align-items: ${(p) => (p.$shouldCenterLabel ? 'center' : 'start')};
  gap: 10px;
`;

interface IndicatorStyleProps {
  $size: `${VisualSizesEnum}`;
  $isRoundedIndicator?: boolean;
  $color?: PaletteColorsEnum;
}

const indicatorSizes = makeSizeConstants(12, 16);
const StyledIndicatorDiv = styled.div<IndicatorStyleProps>`
  flex-shrink: 0;
  position: relative;

  ${center()};

  width: ${(p) => indicatorSizes[p.$size]}px;
  height: ${(p) => indicatorSizes[p.$size]}px;

  border-radius: ${(p) => (p.$isRoundedIndicator ? '50%' : '3px')};

  border: 1px solid ${(p) => p.theme.border.primary};

  &:focus-within {
    outline: 1px solid ${(p) => p.theme.border.focus};
    outline-offset: 1px;
  }

  &[aria-checked='true'],
  &[aria-checked='mixed'] {
    color: ${(p) => p.theme.icon['on-bg-fill']};
    background-color: ${(p) =>
      p.$color ? p.theme.accent[p.$color].highlight : p.theme.background.fill.primary};
  }
  &[aria-disabled='true'] {
    background-color: ${(p) => p.theme.background.fill.primaryDisabled};
    border: 1px solid ${(p) => p.theme.border.alpha};

    &[aria-checked='true'],
    &[aria-checked='mixed'] {
      color: ${(p) => p.theme.icon.disabled};
      background-color: ${(p) => p.theme.background.fill.primaryDisabled};
    }
  }
`;

const StyledBooleanInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${appearanceNone()};
`;

const lineHeight = 20;

interface LabelStyleProps {
  $size: `${VisualSizesEnum}`;
  $shouldCenterLabel?: boolean;
}

const labelStyle = css<LabelStyleProps>`
  color: ${(p) => p.theme.greys.shade80};
  line-height: ${lineHeight}px;
  // Vertically align the label with the indicator. Centered labels are aligned by the wrapper.
  margin-top: ${(p) => (p.$shouldCenterLabel ? 0 : indicatorSizes[p.$size] / 2 - lineHeight / 2)}px;
`;

const indeterminateIndicatorWidths = makeSizeConstants(6, 8);
const StyledIndeterminateCheckedIndicator = styled.span<{$size: `${VisualSizesEnum}`}>`
  width: ${(p) => indeterminateIndicatorWidths[p.$size]}px;
  height: 2px;
  border-radius: 4px;
  background-color: currentColor;
`;

// For single line labels.
const StyledTooltipOverflowCondition = styled(TooltipOverflowCondition)<LabelStyleProps>`
  ${labelStyle}
`;

const StyledMultilineLabelDiv = styled.div<LabelStyleProps>`
  ${labelStyle}
`;

/*
 * Component.
 */

/** Base component to build various boolean inputs. */
export const BooleanInputWithLabel = memo<BooleanInputWithLabelProps>(({children, ...props}) => {
  if (!children) {
    return <BooleanInput {...props} />;
  }

  return (
    <TooltipCoordinator render={!props.isMultiline ? () => children : undefined}>
      <BooleanInput {...props}>
        {!props.isMultiline ? (
          <StyledTooltipOverflowCondition $size={props.size} $shouldCenterLabel={props.shouldCenterLabel}>
            {children}
          </StyledTooltipOverflowCondition>
        ) : (
          <StyledMultilineLabelDiv $size={props.size} $shouldCenterLabel={props.shouldCenterLabel}>
            {children}
          </StyledMultilineLabelDiv>
        )}
      </BooleanInput>
    </TooltipCoordinator>
  );
});

const BooleanInput: FC<BooleanInputWithLabelProps> = (props) => {
  const inputId = useUniqueElementId();

  const {
    className,
    type,
    size,
    checkedIndicator,
    isChecked,
    isDisabled,
    onChange,
    children,
    isIndeterminate,
    isOnRightSide,
    shouldStopPropagation,
  } = props;

  const onBooleanInputChange = useCallback(
    (event: FrontChangeEvent<HTMLInputElement>) => onChange(!isChecked, event),
    [isChecked, onChange],
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const maybeRenderIndicator = () => {
    if (isIndeterminate) {
      return <StyledIndeterminateCheckedIndicator $size={size} />;
    }

    if (isChecked) {
      return checkedIndicator;
    }

    return null;
  };

  useLayoutEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    if (isIndeterminate) {
      input.indeterminate = true;
      input.checked = false;
    } else if (isChecked) {
      input.indeterminate = false;
      input.checked = true;
    }
  }, [isChecked, isIndeterminate]);

  const renderInput = () => (
    <StyledIndicatorDiv
      $size={props.size}
      $isRoundedIndicator={props.isRoundedIndicator}
      $color={props.color}
      aria-checked={isIndeterminate ? 'mixed' : isChecked}
      aria-disabled={isDisabled}
    >
      {maybeRenderIndicator()}
      <StyledBooleanInput
        onClick={(event) => {
          if (shouldStopPropagation) {
            event.stopPropagation();
          }
        }}
        id={inputId}
        type={type}
        checked={isChecked}
        disabled={isDisabled}
        onChange={onBooleanInputChange}
      />
    </StyledIndicatorDiv>
  );

  return (
    <StyledWrapperLabel htmlFor={inputId} className={className} $shouldCenterLabel={props.shouldCenterLabel}>
      {!isOnRightSide && renderInput()}
      {children}
      {isOnRightSide && renderInput()}
    </StyledWrapperLabel>
  );
};
